import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes, type MouseEvent } from 'react';
import { cn } from '@/shared/utils/cn';
import { buttonVariants } from './buttonVariants';
import { Spinner } from './Spinner';

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Renderiza el hijo (p. ej. un <Link>) con el aspecto de botón. */
  asChild?: boolean;
  /** Muestra el indicador y bloquea los clics sin perder el foco (aria-busy). */
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    asChild = false,
    isLoading = false,
    type = 'button',
    onClick,
    children,
    ...rest
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (isLoading) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : type}
      className={cn(buttonVariants({ variant, size }), isLoading && 'cursor-progress', className)}
      aria-busy={isLoading || undefined}
      aria-disabled={isLoading || undefined}
      onClick={handleClick}
      {...rest}
    >
      {asChild ? (
        children
      ) : (
        <>
          {isLoading ? <Spinner size={16} /> : null}
          {children}
        </>
      )}
    </Comp>
  );
});
